import React from 'react';
import { Leaf, Shield, Users, Heart, Info, AlertTriangle } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container-responsive py-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About PKU Tracker
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A comprehensive food tracking application designed specifically for PKU patients, 
            helping manage diet and nutrition with confidence and ease.
          </p>
        </div>

        {/* What is PKU Section */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Info className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">What is PKU?</h2>
            </div>
            
            <div className="prose-custom">
              <p>
                <strong>Phenylketonuria (PKU)</strong> is a rare inherited disorder that affects the body's ability to 
                break down an amino acid called phenylalanine. This amino acid is found in most protein-containing foods 
                and some artificial sweeteners.
              </p>
              
              <p>
                When someone has PKU, their body cannot properly process phenylalanine, which can build up in the blood 
                and brain, potentially causing serious health problems including intellectual disability, behavioral issues, 
                and seizures.
              </p>
              
              <p>
                The primary treatment for PKU is a strict low-protein diet that limits phenylalanine intake. This requires 
                careful monitoring of food choices and protein content to maintain safe phenylalanine levels.
              </p>
            </div>
          </div>
        </div>

        {/* Our Mission Section */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
            </div>
            
            <div className="prose-custom">
              <p>
                At PKU Tracker, we believe that managing a PKU diet shouldn't be overwhelming or isolating. 
                Our mission is to empower PKU patients and their families with the tools and information they need 
                to make informed dietary decisions.
              </p>
              
              <p>
                We're building a comprehensive platform that combines food database management, nutrition tracking, 
                and community support to make PKU diet management more accessible, accurate, and user-friendly.
              </p>
            </div>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Leaf className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Key Features</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Comprehensive Food Database</h3>
                    <p className="text-gray-600 text-sm">
                      Search thousands of foods by name, brand, barcode, or description to find detailed nutrition information.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Protein Exchange Calculator</h3>
                    <p className="text-gray-600 text-sm">
                      Calculate protein exchanges and track your daily phenylalanine intake with precision.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Aspartame Detection</h3>
                    <p className="text-gray-600 text-sm">
                      Identify foods containing aspartame and other artificial sweeteners to avoid.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Free Foods Directory</h3>
                    <p className="text-gray-600 text-sm">
                      Browse foods with less than 0.5g protein that are generally safe for PKU diets.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">5</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Photo Storage</h3>
                    <p className="text-gray-600 text-sm">
                      Store photos of food items, packaging, and nutrition labels for easy reference.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-primary-600 text-sm font-semibold">6</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Location Tracking</h3>
                    <p className="text-gray-600 text-sm">
                      Find where foods are available and track prices across different stores and locations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Safety and Disclaimer Section */}
        <div className="card mb-8 bg-yellow-50 border-yellow-200">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning-600" />
              </div>
              <h2 className="text-2xl font-bold text-warning-900">Important Disclaimer</h2>
            </div>
            
            <div className="prose-custom text-warning-800">
              <p>
                <strong>PKU Tracker is not a substitute for professional medical advice.</strong> 
                The information provided in this application is for educational and informational purposes only.
              </p>
              
              <ul>
                <li>Always consult with your healthcare provider or registered dietitian for personalized dietary advice</li>
                <li>Nutrition information may vary between brands and product formulations</li>
                <li>Verify all nutrition information on actual product packaging</li>
                <li>Individual protein tolerance may vary based on age, weight, and medical condition</li>
                <li>Regular blood phenylalanine monitoring is essential for proper PKU management</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Community Section */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Join Our Community</h2>
            </div>
            
            <div className="prose-custom">
              <p>
                PKU Tracker is more than just an app – it's a community of PKU patients, families, and healthcare 
                professionals working together to improve PKU diet management.
              </p>
              
              <p>
                By contributing food information, photos, and location data, you're helping other PKU patients 
                make better dietary choices. Every contribution makes our database more comprehensive and accurate.
              </p>
            </div>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4">
              <button className="btn btn-primary">
                Start Contributing
              </button>
              <button className="btn btn-outline">
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* Technical Information */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-secondary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Technical Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Built with Modern Technology</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• React 18 with modern hooks</li>
                  <li>• Cloudflare Workers backend</li>
                  <li>• Cloudflare D1 database</li>
                  <li>• Cloudflare R2 storage</li>
                  <li>• Mobile-first responsive design</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Data & Privacy</h3>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• All data is encrypted in transit</li>
                  <li>• No personal health information stored</li>
                  <li>• Open source and transparent</li>
                  <li>• Regular security audits</li>
                  <li>• GDPR compliant</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
